import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { UpdateMaterialIssueRequest, IssueReturnMaterialRequest } from '@/types/workOrders';
import { WorkOrderBaseState } from './workOrderBase';

/**
 * Helper function to create a Purchase Requisition when stock falls below reorder level
 * Uses the proper PR table structure with pr_line for line items
 */
async function createPurchaseRequisitionForShortage(
  currentMaterial: { material_id: string; bom_component_id: string; id: string },
  inventoryItem: { id: string; stock_level: number | null; reorder_level: number | null; code: string; name: string },
  newStockLevel: number,
  workOrderId: string,
  userId: string | undefined
): Promise<{ success: boolean; prId?: string; error?: string }> {
  try {
    // Generate PR number
    const prNumber = `PR-WO-${Date.now().toString(36).toUpperCase()}`;
    
    // Calculate quantity to order (reorder level - current + 20% buffer)
    const reorderLevel = inventoryItem.reorder_level || 0;
    const qtyToOrder = Math.ceil(reorderLevel - newStockLevel + (reorderLevel * 0.2));
    
    // Get a default department for production PRs
    const { data: departments } = await supabase
      .from('departments')
      .select('id')
      .limit(1)
      .single();
    
    const departmentId = departments?.id;
    
    if (!departmentId) {
      console.error('No department found for PR creation');
      return { success: false, error: 'No department found' };
    }

    // Create the purchase requisition header with work_order_id linkage
    const { data: prData, error: prError } = await supabase
      .from('purchase_requisitions')
      .insert([{
        pr_number: prNumber,
        requested_by: userId,
        department_id: departmentId,
        purpose: `Auto-generated PR for ${inventoryItem.name || inventoryItem.code} - stock below reorder level after WO material issue`,
        status: 'Draft',
        required_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week from now
        notes: `Stock level (${newStockLevel}) fell below reorder level (${reorderLevel}) after issuing materials from work order.`,
        work_order_id: workOrderId,
        origin_type: 'work_order_material_shortage'
      }])
      .select('id')
      .single();

    if (prError) {
      console.error('Error creating PR header:', prError);
      return { success: false, error: prError.message };
    }

    // Create the PR line item with work_order_material_id linkage
    const { error: lineError } = await supabase
      .from('pr_line')
      .insert([{
        pr_id: prData.id,
        item_id: currentMaterial.bom_component_id, // Links to bom_components
        qty: qtyToOrder,
        uom: 'pcs',
        work_order_material_id: currentMaterial.id // Links back to the WO material that triggered this
      }]);

    if (lineError) {
      console.error('Error creating PR line:', lineError);
      // PR header was created, but line failed - still return success with warning
      return { success: true, prId: prData.id, error: `PR created but line item failed: ${lineError.message}` };
    }

    return { success: true, prId: prData.id };
  } catch (error: any) {
    console.error('Error in createPurchaseRequisitionForShortage:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch stock availability for an inventory item across all warehouses
 */
async function fetchStockByWarehouse(inventoryItemId: string): Promise<Array<{
  warehouse_id: string;
  warehouse_name: string;
  warehouse_code: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
}>> {
  try {
    const { data, error } = await supabase
      .from('warehouse_stock')
      .select(`
        warehouse_id,
        quantity,
        reserved_quantity,
        available_quantity,
        warehouse:warehouse_id(id, name, code)
      `)
      .eq('item_id', inventoryItemId);

    if (error || !data) return [];

    return data.map(ws => ({
      warehouse_id: ws.warehouse_id,
      warehouse_name: (ws.warehouse as any)?.name || 'Unknown',
      warehouse_code: (ws.warehouse as any)?.code || '',
      quantity: parseFloat(ws.quantity as any) || 0,
      reserved_quantity: parseFloat(ws.reserved_quantity as any) || 0,
      available_quantity: parseFloat(ws.available_quantity as any) || 0
    })).filter(ws => ws.quantity > 0 || ws.available_quantity > 0);
  } catch {
    return [];
  }
}

export const useWorkOrderMaterials = (baseState: WorkOrderBaseState) => {
  const {
    loading,
    setLoading,
    toast
  } = baseState;

  /**
   * Fetch stock availability for a material across all warehouses
   * This is used by the IssueMaterialModal to show where stock is available
   */
  const fetchMaterialStockByWarehouse = useCallback(async (materialId: string) => {
    try {
      // First get the inventory_item_id from work_order_materials
      const { data: material, error: matError } = await supabase
        .from('work_order_materials')
        .select('material_id')
        .eq('id', materialId)
        .single();

      if (matError || !material?.material_id) {
        console.warn('Material not linked to inventory item:', materialId);
        return [];
      }

      return await fetchStockByWarehouse(material.material_id);
    } catch (error) {
      console.error('Error fetching material stock by warehouse:', error);
      return [];
    }
  }, []);

  /**
   * Fetch Materials for a Work Order
   */
  const fetchWorkOrderMaterials = useCallback(async (workOrderId: UUID) => {
    try {
      setLoading(true);
      
      // Join with bom_components, warehouse, and inventory_items to get full material details
      // Include material, raw_material_size, finishing_size for better descriptions
      const { data, error } = await supabase
        .from('work_order_materials')
        .select(`
          *,
          bom_component:bom_components(
            component_name,
            component_type,
            drawing_number,
            material,
            raw_material_size,
            finishing_size,
            unit,
            specifications
          ),
          warehouse:warehouse(
            id,
            name,
            code
          )
        `)
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Map the data to include the material details from bom_component
      // Map database column names to TypeScript interface field names for UI convenience
      const materialsWithDetails = data?.map(material => {
        const bc = material.bom_component;
        
        // Build a meaningful description from available fields
        let description = '';
        if (bc?.material) {
          description = bc.material;
        }
        if (bc?.raw_material_size) {
          description += description ? ` | Size: ${bc.raw_material_size}` : `Size: ${bc.raw_material_size}`;
        }
        if (bc?.finishing_size) {
          description += description ? ` → ${bc.finishing_size}` : `Finish: ${bc.finishing_size}`;
        }
        
        return {
          ...material,
          // Map database column names to UI-friendly field names
          required_qty: material.required_quantity || 0,
          available_qty: material.available_quantity || 0,
          issued_qty: material.issued_quantity || 0,
          returned_qty: material.returned_quantity || 0,
          // Material item details from BOM component
          item_name: bc?.component_name && bc?.component_name !== ''
            ? bc?.component_name
            : `Material ${material.id.slice(0, 8)}`,
          item_code: bc?.drawing_number && bc?.drawing_number !== ''
            ? bc?.drawing_number
            : bc?.component_name || `MAT-${material.id.slice(0, 8)}`,
          // Legacy combined description for backward compatibility
          item_description: description || (bc?.component_type === 'raw_material' ? 'Raw Material' : ''),
          // Separate material specification fields for clear display
          material_type: bc?.material || null,           // e.g., "Steel 4140", "Bronze C932"
          raw_material_size: bc?.raw_material_size || null, // Size inventory provides, e.g., "Φ50 x 90mm"
          finishing_size: bc?.finishing_size || null,    // Final size after machining, e.g., "Φ45 x 80mm"
          uom: bc?.unit || material.uom || 'pcs',
          // Warehouse details
          warehouse_name: material.warehouse?.name || null
        };
      }) || [];
      
      return materialsWithDetails;
    } catch (error: any) {
      toast({
        title: 'Error fetching materials',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Update material issue quantity
   */
  const updateMaterialIssue = useCallback(async (materialIssue: UpdateMaterialIssueRequest) => {
    try {
      setLoading(true);
      
      // First, get the current material info
      const { data: currentMaterial, error: fetchError } = await supabase
        .from('work_order_materials')
        .select('*')
        .eq('id', materialIssue.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Calculate the new status based on required and issued quantities
      // Use database column names since currentMaterial comes directly from database
      let status = 'Not Issued';
      if (materialIssue.issued_quantity >= currentMaterial.required_quantity) {
        status = 'Fully Issued';
      } else if (materialIssue.issued_quantity > 0) {
        status = 'Partially Issued';
      }
      
      // Update the material issue record
      // Use database column names for the update operation
      const { data, error } = await supabase
        .from('work_order_materials')
        .update({
          // Database column name is issued_quantity
          issued_quantity: materialIssue.issued_quantity,
          status
        })
        .eq('id', materialIssue.id)
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Material updated',
        description: 'Material issue has been updated successfully.',
        variant: 'success',
      });
      
      // Refresh the materials for this work order
      await fetchWorkOrderMaterials(data.work_order_id);
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error updating material issue',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderMaterials, setLoading, toast]);

  /**
   * Issue or Return Material
   */
  const issueMaterial = useCallback(async (materialRequest: IssueReturnMaterialRequest) => {
    try {
      setLoading(true);
      
      // First, get the current material info
      const { data: currentMaterial, error: fetchError } = await supabase
        .from('work_order_materials')
        .select('*')
        .eq('id', materialRequest.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Calculate the new issued and returned quantities
      // Use database column names since currentMaterial comes directly from database
      let newIssuedQuantity = currentMaterial.issued_quantity || 0;
      let newReturnedQuantity = currentMaterial.returned_quantity || 0;

      if (materialRequest.transaction_type === 'issue') {
        newIssuedQuantity += materialRequest.quantity;
      } else {
        // For returns, we track in returned_quantity and reduce net issued
        newReturnedQuantity += materialRequest.quantity;
        // Ensure we don't return more than issued
        newReturnedQuantity = Math.min(newReturnedQuantity, newIssuedQuantity);
      }

      // Calculate net issued (issued - returned) for status calculation
      const netIssuedQuantity = newIssuedQuantity - newReturnedQuantity;

      // Calculate the new status based on required and net issued quantities
      // Use database column names since currentMaterial comes directly from database
      let status = 'Not Issued';
      if (netIssuedQuantity >= currentMaterial.required_quantity) {
        status = 'Fully Issued';
      } else if (netIssuedQuantity > 0) {
        status = 'Partially Issued';
      }

      // Update the material issue record
      // Use database column names for the update operation
      const { data, error } = await supabase
        .from('work_order_materials')
        .update({
          issued_quantity: newIssuedQuantity,
          returned_quantity: newReturnedQuantity,
          status
        })
        .eq('id', materialRequest.id)
        .select()
        .single();

      if (error) throw error;
      
      // Create inventory transaction record for automatic stock updates via database triggers
      // This uses the inventory_tx table which has triggers to update warehouse_stock and inventory_items.stock_level
      if (currentMaterial.material_id) {
        const currentUser = (await supabase.auth.getUser()).data.user;
        
        // Determine transaction type based on issue/return
        const txnType = materialRequest.transaction_type === 'issue' ? 'ISSUE-TO-PRODUCTION' : 'WIP_RETURN';
        
        // Get the inventory item details for the transaction
        const { data: inventoryItem, error: inventoryError } = await supabase
          .from('inventory_items')
          .select('id, stock_level, reorder_level, code, name, uom_id, uoms(code)')
          .eq('id', currentMaterial.material_id)
          .single();

        if (inventoryError) {
          console.error('Error fetching inventory item:', inventoryError);
        }

        // Create inventory_tx record - triggers will automatically update stock levels
        const { error: txError } = await supabase
          .from('inventory_tx')
          .insert([{
            txn_type: txnType,
            inventory_item_id: currentMaterial.material_id, // Links to inventory_items for trigger
            item_id: currentMaterial.bom_component_id, // Legacy link to bom_components
            qty: materialRequest.quantity,
            uom: materialRequest.uom || (inventoryItem?.uoms as any)?.code || 'pcs',
            wh_from: materialRequest.transaction_type === 'issue' ? materialRequest.location_id : null,
            wh_to: materialRequest.transaction_type === 'return' ? materialRequest.location_id : null,
            ref_table: 'work_order_materials',
            ref_id: materialRequest.id,
            note: materialRequest.notes || `${txnType} for Work Order`,
            created_by: currentUser?.id
          }]);

        if (txError) {
          console.error('Error creating inventory transaction:', txError);
          // Don't throw - the material update succeeded, just log the inventory tx error
        }
          
        // If this is an issue transaction, check inventory levels and create PR if needed
        if (materialRequest.transaction_type === 'issue' && inventoryItem) {
          // Calculate new stock level after this issue
          const newStockLevel = (inventoryItem.stock_level || 0) - materialRequest.quantity;
          
          if (newStockLevel < (inventoryItem.reorder_level || 0)) {
            // Create a purchase requisition using the proper PR table structure
            const prResult = await createPurchaseRequisitionForShortage(
              currentMaterial,
              inventoryItem,
              newStockLevel,
              data.work_order_id,
              currentUser?.id
            );
            
            if (prResult.success) {
              toast({
                title: 'Purchase Requisition Created',
                description: `Auto-generated PR for ${inventoryItem.name || inventoryItem.code} as stock level fell below reorder point.`,
                variant: 'default',
              });
            }
          }
        }
      }
      
      toast({
        title: materialRequest.transaction_type === 'issue' ? 'Material issued' : 'Material returned',
        description: `${materialRequest.quantity} ${materialRequest.uom || 'units'} ${materialRequest.transaction_type === 'issue' ? 'issued to' : 'returned from'} work order.`,
        variant: 'success',
      });
      
      // Refresh the materials for this work order
      await fetchWorkOrderMaterials(data.work_order_id);
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error processing material',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderMaterials, setLoading, toast]);

  /**
   * Handle Subcontracted Operation
   */
  const handleSubcontractedOperation = useCallback(async (subcontractRequest: {
    operation_id: string;
    vendor_id: string;
    expected_completion_date: string;
    instructions?: string;
    materials_to_issue?: Array<{
      material_id: string;
      quantity: number;
    }>;
  }) => {
    try {
      setLoading(true);
      
      // First, get the operation
      const { data: operation, error: fetchError } = await supabase
        .from('work_order_operations')
        .select('*')
        .eq('id', subcontractRequest.operation_id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update the operation with subcontract info
      const { data, error } = await supabase
        .from('work_order_operations')
        .update({
          is_subcontracted: true,
          subcontractor_id: subcontractRequest.vendor_id,
          notes: (operation.notes || '') + '\n' + (subcontractRequest.instructions || '')
        })
        .eq('id', subcontractRequest.operation_id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Issue materials to the subcontractor if needed
      if (subcontractRequest.materials_to_issue && subcontractRequest.materials_to_issue.length > 0) {
        for (const material of subcontractRequest.materials_to_issue) {
          // Get the material from work order materials
          const { data: workOrderMaterial } = await supabase
            .from('work_order_materials')
            .select('*')
            .eq('material_id', material.material_id)
            .eq('work_order_id', operation.work_order_id)
            .single();
          
          if (workOrderMaterial) {
            // Create a material issue record
            await issueMaterial({
              id: workOrderMaterial.id,
              quantity: material.quantity,
              transaction_type: 'issue',
              location_id: 'subcontract', // This would need to be a real location ID in your system
              notes: `Issued to subcontractor: ${subcontractRequest.vendor_id}`
            });
          }
        }
      }
      
      toast({
        title: 'Subcontracted operation',
        description: 'Operation has been marked as subcontracted and materials have been issued.',
        variant: 'success',
      });
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error with subcontracted operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [issueMaterial, setLoading, toast]);

  return {
    loading,
    fetchWorkOrderMaterials,
    fetchMaterialStockByWarehouse,
    updateMaterialIssue,
    issueMaterial,
    handleSubcontractedOperation
  };
};
